import { useQueryClient } from '@tanstack/react-query';
import { f7ready } from 'framework7-react';
import { FC, useMemo, useRef, useState } from 'react';

import * as dayjs from 'dayjs';

import { useSearchQuery } from '@queries/UserQueries';

import Button from '@components/buttons/Button';
import { ChipRadioOption } from '@components/chips/ChipRadioOptions';
import { InlineDatePicker } from '@components/layout/InlineDatePicker/InlineDatePicker';
import { Popup } from '@components/layout/Popup/Popup';

type DateRange = [string, string];

const DateRangePickerPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  onUpdateEventFilter: (dateRange: DateRange) => void;
}> = ({ isVisible, onClose, onUpdateEventFilter }) => {
  const [dateRange, setDateRange] = useState<DateRange>();
  const [startDate, endDate] = dateRange ?? [];

  const handleDateRangeChange = (updatedDateRange) => {
    const [firstValue, secondValue] = Object.keys(updatedDateRange);
    setDateRange(firstValue && secondValue ? [firstValue, secondValue] : undefined);
  };

  const handleUpdateEventFilter = () => {
    if (dateRange) {
      onUpdateEventFilter(dateRange);
      onClose();
    }
  };

  return (
    <Popup isVisible={isVisible} onClose={onClose}>
      <Popup.Content>
        <InlineDatePicker
          isRangePicker
          value={startDate && endDate ? { [startDate]: true, [endDate]: true } : {}}
          onChange={handleDateRangeChange}
        />
        <Button
          className="mt-2"
          disabled={!dateRange}
          roundness="rounded"
          onClick={handleUpdateEventFilter}
        >
          View Date Range
        </Button>
      </Popup.Content>
    </Popup>
  );
};

const EVENT_FILTER_TYPES = ['today', 'tomorrow', 'thisWeekend', 'custom'] as const;
export type EventDateFilter = (typeof EVENT_FILTER_TYPES)[number];

const EVENT_FILTERS: Record<
  EventDateFilter,
  {
    label: string;
    getRange: () => DateRange;
  }
> = {
  today: {
    label: 'Today',
    getRange: () => {
      const today = dayjs().format('YYYY-MM-DD');
      return [today, today];
    },
  },
  tomorrow: {
    label: 'Tomorrow',
    getRange: () => {
      const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
      return [tomorrow, tomorrow];
    },
  },
  thisWeekend: {
    label: 'This weekend',
    getRange: () => {
      const friday = dayjs().day(5).format('YYYY-MM-DD');
      const sunday = dayjs().day(5).add(2, 'days').format('YYYY-MM-DD');

      return [friday, sunday];
    },
  },
  custom: {
    label: 'Choose date range',
    getRange: () => {
      const today = dayjs().format('YYYY-MM-DD');
      const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');

      return [today, tomorrow];
    },
  },
};

export interface EventDateFilterOption extends Omit<ChipRadioOption, 'value'> {
  value: EventDateFilter;
  getRange: () => DateRange;
}

interface EventFilters {
  dateRange?: DateRange;
}

export const useEventFilters = (initialFilters: Partial<EventFilters> = {}) => {
  const queryClient = useQueryClient();
  const [eventFilters, setEventFilters] = useState<EventFilters>(initialFilters);
  const [datePopupVisible, setDatePopupVisible] = useState<boolean>(false);

  const eventDateFilterOptions: EventDateFilterOption[] = useMemo(
    () =>
      Object.entries(EVENT_FILTERS).map(([value, { label, getRange }]) => ({
        value: value as EventDateFilter,
        label,
        getRange,
        onClick: value === 'custom' ? () => setDatePopupVisible(true) : undefined,
      })),
    [],
  );

  const dateFilter = useMemo(() => {
    const [currentFilterStartDate, currentFilterEndDate] = eventFilters?.dateRange ?? [];

    if (!currentFilterStartDate || !currentFilterEndDate) {
      return undefined;
    }

    const [value] = Object.entries(EVENT_FILTERS)
      .filter(([value]) => value !== 'custom')
      .find(([_, { getRange }]) => {
        const [startDate, endDate] = getRange();
        return startDate === currentFilterStartDate && endDate === currentFilterEndDate;
      }) ?? ['custom'];

    return value as EventDateFilter;
  }, [eventFilters?.dateRange]);

  const currentDateFilter = useMemo(() => EVENT_FILTERS?.[dateFilter], [dateFilter]);

  const updateDateFilter = (dateRange: DateRange) => {
    setEventFilters((prev) => ({
      ...prev,
      dateRange,
    }));
  };

  const refetchEvents = async () => {
    const promises = Object.entries(EVENT_FILTERS)
      .filter(([value]) => value !== 'custom')
      .map(([_, { getRange }]) => {
        const [startDate, endDate] = getRange();
        queryClient.invalidateQueries({
          queryKey: useSearchQuery.queryKey({
            startDate,
            endDate,
          }),
        });
      });

    await Promise.all(promises);
  };

  const dateRangePickerPopup = useMemo(
    () => (
      <DateRangePickerPopup
        isVisible={datePopupVisible}
        onClose={() => setDatePopupVisible(false)}
        onUpdateEventFilter={updateDateFilter}
      />
    ),
    [datePopupVisible],
  );

  return {
    refetchEvents,
    eventFilters: { ...eventFilters, dateFilter },
    currentDateFilter,
    updateDateFilter,
    eventDateFilterOptions,
    dateRangePickerPopup,
  };
};
